function addElmSelectionOnCopy(elm: HTMLTextAreaElement, value: string) {
    elm.value = value;
    document.body.appendChild(elm);
    elm.style.top = '0';
    elm.style.left = '0';
    elm.style.position = 'fixed';
    elm.style.width = '0';
    elm.style.height = '0';
    elm.select();
}

// The values here should be either a string or Blob AND the PromiseLike that matches
// ex: 'mime/type': blob | PromiseLike<Blob> OR 'mime/type': string | PromiseLike<string>
// Blobs will not work with the fallback implementation
interface CopyDataType {
    'text/plain': string | PromiseLike<string>;
    'text/html'?: string | PromiseLike<string>;
}

type ClipboardItemType = Record<string, string | Blob | PromiseLike<string | Blob>>;

const ClipboardItemAvailable = window.hasOwnProperty('ClipboardItem');

export default async function copyHelper(data: CopyDataType, copyLinkCallback?: () => void) {
    try {
        await copyHelperInternal(data, false, copyLinkCallback);
    } catch (error) {
        await copyHelperInternal(data, true, copyLinkCallback);
    }
}

async function copyHelperInternal(
    data: CopyDataType,
    forceFallback: boolean,
    copyLinkCallback?: () => void
) {
    // Check to see if clipboard APIs exist (older browsers and Firefox without the right flags on don't have it) or if we are retrying
    // fallback to execCommand if we don't have clipboard APIs or are retrying
    if (!navigator.clipboard || !ClipboardItemAvailable || forceFallback) {
        // Convert all the potential promises to just strings for later usage.
        const clipboardDataItems: {
            [key in keyof CopyDataType]?: string;
        } = {};
        await Promise.all(
            Object.keys(data).map(async key => {
                const value = data[key as keyof CopyDataType] as
                    | string
                    | Blob
                    | PromiseLike<string | Blob>;
                if (typeof value == 'string') {
                    clipboardDataItems[key as keyof CopyDataType] = value;
                } else if ('then' in value) {
                    // if promise
                    const promiseValue = await (value as PromiseLike<string | Blob>);
                    if (typeof promiseValue == 'string') {
                        clipboardDataItems[key as keyof CopyDataType] = promiseValue;
                    }
                    // else don't care about blobs at the moment
                }
                // else don't care about blobs at the moment
            })
        );

        const listener = (e: ClipboardEvent) => {
            const clipboard =
                e.clipboardData || ((window as any)['clipboardData'] as DataTransfer | null);

            // Set the plain data for targets that don't accept a text/html result (VS Code, Notepad, etc.)
            if (clipboardDataItems['text/plain']) {
                clipboard?.setData('text/plain', clipboardDataItems['text/plain']);
            }

            // Set the rich data for targets that do accept a text/html result (Outlook, Teams, etc.)
            if (clipboardDataItems['text/html']) {
                clipboard?.setData('text/html', clipboardDataItems['text/html']);
            }
            e.preventDefault();
        };

        // We can trigger the above function with the right access by adding a listener,
        document.addEventListener('copy', listener, false);
        // creating elm to allow for Webkit/Safari to clip the booking page link to the copy clipboard
        const elm = document.createElement('textarea');
        addElmSelectionOnCopy(elm, data['text/plain'] as string);
        // triggering the event ourselves,
        document.execCommand('copy');
        // and finally cleaning up after ourselves.
        document.removeEventListener('copy', listener, false);
        //Remove the element
        document.body.removeChild(elm);

        copyLinkCallback?.();
    } else {
        const clipboardDataItems: ClipboardItemType = {};
        /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
         * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
         *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
        Object.keys(data).forEach(key => {
            const value = data[key as keyof CopyDataType] as
                | string
                | Blob
                | PromiseLike<string | Blob>;
            clipboardDataItems[key] = Promise.resolve(value).then(promiseValue => {
                if (typeof promiseValue == 'string') {
                    return new Blob([promiseValue], { type: key });
                } else {
                    return promiseValue;
                }
            });
        });
        const clipboardData = new ClipboardItem(clipboardDataItems);

        try {
            await navigator.clipboard.write([clipboardData]);
        } finally {
            copyLinkCallback?.();
        }
    }
}
